import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { IconQrcode, IconCopy, IconClockHour4, IconAlertTriangle, IconRefresh, IconX, IconLink } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';

// Shared "upload from mobile" QR modal — ONE design used everywhere (AI editor,
// Manual editor, …). This is the /generate-image (AI editor) design. Purely
// presentational: the parent owns the QR session state and the refresh/close handlers.
//
// Props:
//   open       – whether the modal is shown
//   onClose    – called when the user dismisses it
//   status     – 'loading' | 'ready' | 'expired'
//   qrDataUrl  – data URL of the rendered QR image (when ready)
//   mobileUrl  – the mobile-upload link the QR encodes (copied by the copy-link button)
//   timeLeft   – seconds until the session expires
//   onRefresh  – called to (re)generate a QR session
export default function QrUploadModal({ open, onClose, status, qrDataUrl, mobileUrl, timeLeft, onRefresh }) {
    const { t } = useTranslation();

    // Backdrop click can't close the modal — instead give a subtle shrink "bounce" so
    // the user gets feedback that the ✕ / Esc is the way out.
    const [nudging, setNudging] = useState(false);
    const triggerNudge = () => {
        setNudging(false);
        requestAnimationFrame(() => {
            setNudging(true);
            setTimeout(() => setNudging(false), 240);
        });
    };

    const formatQrTime = (s) =>
        `${Math.floor((s || 0) / 60).toString().padStart(2, '0')}:${((s || 0) % 60).toString().padStart(2, '0')}`;

    const copyQrImage = async () => {
        try {
            const res = await fetch(qrDataUrl);
            const blob = await res.blob();
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            notifications.show({ message: t('imageGeneration.qrCopied'), color: 'green', autoClose: 2000 });
        } catch {
            notifications.show({ message: t('imageGeneration.qrCopyFailed'), color: 'red', autoClose: 2000 });
        }
    };

    // Copy the mobile-upload link. Async Clipboard API when available (secure context),
    // else an off-screen textarea + execCommand (works on http/LAN insecure contexts).
    const copyLink = () => {
        if (!mobileUrl) return;
        const ok = () => notifications.show({ message: t('imageGeneration.qrLinkCopied'), color: 'green', autoClose: 2000 });
        const fail = () => notifications.show({ message: t('imageGeneration.qrCopyFailed'), color: 'red', autoClose: 2000 });
        const legacyCopy = () => {
            try {
                const ta = document.createElement('textarea');
                ta.value = mobileUrl;
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.focus(); ta.select();
                const done = document.execCommand('copy');
                document.body.removeChild(ta);
                done ? ok() : fail();
            } catch { fail(); }
        };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(mobileUrl).then(ok).catch(legacyCopy);
        } else {
            legacyCopy();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className={`max-w-[390px] px-6 py-4 ${nudging ? 'qr-nudge' : ''}`} showClose={false} onInteractOutside={(e) => { e.preventDefault(); triggerNudge(); }}>
                <style>{`
                    @keyframes qr-dot-pulse {0%{box-shadow:0 0 0 0 rgba(16,185,129,0.55)}70%{box-shadow:0 0 0 6px rgba(16,185,129,0)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}
                    @keyframes qr-ring-glow {0%,100%{box-shadow:0 6px 22px rgba(230,138,0,0.22)}50%{box-shadow:0 6px 28px rgba(230,138,0,0.36)}}
                    @keyframes qr-scan {0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}
                    .qr-dot{width:8px;height:8px;border-radius:9999px;background:#10b981;animation:qr-dot-pulse 1.6s ease-out infinite;flex-shrink:0}
                    .qr-scanline{position:absolute;top:0;left:0;height:2px;width:25%;background:linear-gradient(90deg,transparent,#10b981,transparent);animation:qr-scan 2.2s ease-in-out infinite}
                    .qr-ring{animation:qr-ring-glow 3s ease-in-out infinite}
                    @keyframes qr-nudge {0%,100%{transform:translate(-50%,-50%) scale(1)}50%{transform:translate(-50%,-50%) scale(0.96)}}
                    .qr-nudge{animation:qr-nudge 0.22s ease-in-out}
                `}</style>

                {/* Header: title + red close button */}
                <div className="flex items-center justify-between mb-2">
                    <DialogTitle>
                        <div className="flex items-center gap-1.5">
                            <IconQrcode size={18} />
                            <span className="text-[15px]">{t('imageGeneration.qrModalTitle')}</span>
                        </div>
                    </DialogTitle>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <DialogClose asChild>
                                <button aria-label="Close" className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shrink-0 transition-colors">
                                    <IconX size={15} />
                                </button>
                            </DialogClose>
                        </TooltipTrigger>
                        <TooltipContent side="left">{t('common.close')} (Esc)</TooltipContent>
                    </Tooltip>
                </div>

                <div className="flex flex-col items-center w-full">
                    {status === 'loading' && (
                        <div className="flex flex-col items-center gap-2.5 w-full">
                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full h-9 w-9" />
                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded h-3 w-52" />
                            <div className="rounded-2xl p-1 bg-gradient-to-br from-[#FBA53A] to-[#E68A00]">
                                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-[15px] h-[190px] w-[190px]" />
                            </div>
                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-lg h-9 w-full" />
                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-lg h-10 w-full" />
                        </div>
                    )}

                    {status === 'ready' && qrDataUrl && (
                        <div className="flex flex-col items-center w-full">
                            {/* Instruction */}
                            <p className="text-[12.5px] font-medium text-zinc-800 dark:text-zinc-100 text-center leading-snug mb-2.5 px-1">
                                {t('imageGeneration.qrInstruction')}
                            </p>

                            {/* QR Code */}
                            <div className="relative inline-block mb-2.5">
                                <div className="qr-ring rounded-2xl p-1 bg-gradient-to-br from-[#FBA53A] to-[#E68A00]">
                                    <div className="rounded-[15px] p-2 bg-white">
                                        <img src={qrDataUrl} alt="QR Code" className="block w-[190px] h-[190px] rounded-md" />
                                    </div>
                                </div>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            className="absolute -top-2 -right-2 w-[30px] h-[30px] rounded-full bg-[#e68a00] border-[3px] border-white dark:border-zinc-900 text-white flex items-center justify-center cursor-pointer shadow-[0_4px_12px_rgba(0,0,0,0.22)] transition-transform hover:-translate-y-0.5 hover:scale-105"
                                            onClick={copyQrImage}
                                        >
                                            <IconCopy size={14} />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{t('imageGeneration.qrCopyImage')}</TooltipContent>
                                </Tooltip>
                            </div>

                            {/* Copy-link button (no URL shown) */}
                            {mobileUrl && (
                                <button
                                    type="button"
                                    onClick={copyLink}
                                    className="w-full inline-flex items-center justify-center gap-1.5 mb-2 px-4 py-1.5 rounded-lg bg-[#e68a00] hover:bg-[#d67e00] text-white text-[13px] font-semibold cursor-pointer transition-colors"
                                >
                                    <IconLink size={14} />
                                    {t('imageGeneration.qrCopyLink')}
                                </button>
                            )}

                            {/* Live status panel: waiting + timer + do-not-close hint */}
                            <div className="w-full relative overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 mb-2">
                                <span className="qr-scanline" />
                                <div className="flex items-center justify-between gap-2.5 px-3 py-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="qr-dot" />
                                        <span className="text-[12.5px] font-medium text-zinc-800 dark:text-zinc-100 truncate">{t('imageGeneration.qrWaiting')}</span>
                                    </div>
                                    <span className={`flex items-center gap-1.5 text-[12.5px] font-bold tabular-nums px-2.5 py-1 rounded-full shrink-0 ${timeLeft < 60 ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-600'}`}>
                                        <IconClockHour4 size={13} /> {formatQrTime(timeLeft)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-2 border-t border-zinc-200 dark:border-zinc-700 bg-red-500/[0.05]">
                                    <IconAlertTriangle size={13} color="#ef4444" className="shrink-0" />
                                    <p className="text-[11px] text-rose-600 font-medium leading-snug m-0">
                                        {t('imageGeneration.qrDoNotClose')}
                                    </p>
                                </div>
                            </div>

                            {/* Refresh button */}
                            <button
                                className="w-full inline-flex items-center justify-center gap-2 font-semibold text-[13px] px-4 py-1.5 rounded-lg bg-transparent text-zinc-800 dark:text-zinc-100 border-[1.5px] border-zinc-200 dark:border-zinc-700 cursor-pointer transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800"
                                onClick={onRefresh}
                            >
                                <IconRefresh size={15} />
                                {t('imageGeneration.qrRefresh')}
                            </button>
                        </div>
                    )}

                    {status === 'expired' && (
                        <div className="flex flex-col items-center gap-3 pt-2 pb-1">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                                <IconClockHour4 size={26} color="#ef4444" />
                            </div>
                            <p className="text-[13px] font-medium text-zinc-800 dark:text-zinc-100 text-center max-w-[240px] leading-snug m-0">
                                {t('imageGeneration.qrExpiredTitle')}
                            </p>
                            <button
                                className="inline-flex items-center gap-1.5 font-semibold text-[13px] px-5 py-2 rounded-full text-white border-none cursor-pointer bg-gradient-to-br from-[#FBA53A] to-[#E68A00] shadow-[0_4px_14px_rgba(230,138,0,0.35)]"
                                onClick={onRefresh}
                            >
                                <IconRefresh size={15} />
                                {t('imageGeneration.qrRefresh')}
                            </button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
